import { NodeProps } from '@xyflow/react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import DataNode from './data/DataNode';
import FilterNode from './process/FilterNode';
import JoinNode from './process/JoinNode';
import ControlNode from './control/ControlNode';
import WorkflowNode from './WorkflowNode';
import PlaceholderNode from './PlaceholderNode';
import GroupNode from './GroupNode';
import MemoNode from './MemoNode';
import TransformLoadNode from './process/TransformLoadNode';
import ExecNode from './exec/ExecNode';
import AnalyticsNode from './process/AnalyticsNode';
import QualityNode from './process/QualityNode';
import React from 'react';
import { NodeTypes } from '@xyflow/react';
import styles from './WorkflowNode.module.css';

export const nodeTypes: Record<string, React.ComponentType<any>> = {
  csvData: DataNode,
  rdbData: DataNode,
  filter: FilterNode,
  join: JoinNode,
  branch: ControlNode,
  merge: ControlNode,
  userConfirm: ControlNode,
  workflow: WorkflowNode,
  placeholder: PlaceholderNode,
  group: GroupNode,
  subflow: GroupNode,
  repeatGroup: GroupNode,
  innerData: DataNode,
  memo: MemoNode,
  transformLoad: TransformLoadNode,
  runWorkflow: ExecNode,
  shellCommand: ExecNode,
  sqlScript: ExecNode,
  aggregate: AnalyticsNode,
  compare: AnalyticsNode,
  sort: AnalyticsNode,
  lookup: AnalyticsNode,
  quality: QualityNode,
};

export const edgeTypes = {
  workflow: () => null, // 엣지 컴포넌트 (필요 시 구현)
};

export { default as WorkflowNode } from './WorkflowNode';
export { default as PlaceholderNode } from './PlaceholderNode';
export { default as ControlNode } from './control/ControlNode';

// 호버 하이라이트를 입힌 노드 타입 — 모듈 로드 시 한 번만 만든다.
// 예전 createMemoizedNodeTypes(hoveredNodeId) 는 호버 노드가 바뀔 때마다 nodeTypes 객체를 새로 만들어
// React Flow 가 모든 노드를 리마운트했다 (경고 #002, 드래그 중 끊김의 원인). 이제 각 래퍼가
// hoveredNodeId 를 스토어에서 직접 읽으므로 타입 객체는 항상 동일한 참조를 유지한다.
function createHoverAwareNodeTypes(): NodeTypes {
  const types: NodeTypes = {};
  Object.entries(nodeTypes).forEach(([key, Component]) => {
    const Wrapped = (props: any) => {
      const hoveredNodeId = useSelector((state: RootState) => state.workflow.hoveredNodeId);
      const isHighlighted = !!hoveredNodeId && props.id === hoveredNodeId;
      return React.createElement(Component as any, {
        ...props,
        className: isHighlighted ? `${props.className || ''} ${styles.highlight}`.trim() : props.className,
        isHovered: isHighlighted,
        data: { ...props.data, isHighlighted },
      });
    };
    Wrapped.displayName = `HoverAware(${key})`;
    types[key] = React.memo(Wrapped);
  });
  return types;
}

export const hoverAwareNodeTypes: NodeTypes = createHoverAwareNodeTypes();
